import { PaginationFilter } from '@liveauctioneers/caterwaul-components/types/PaginationFilter';
import { Bell } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import styled from 'styled-components';

import SearchFilter from '@/components/Pagination/hammer-pagination/SearchFilter';
import SortPageSizePair from '@/components/Pagination/hammer-pagination/SortPageSizePair';
import { ManageSavedSearchSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getTotalSavedSearches, getTotalSavedSearchItems } from '@/redux/modules/savedSearch';
import { getNotificationsUrl } from '@/utils/urls';

type Props = {
    filters: PaginationFilter;
    onFilterChange: (filters: PaginationFilter) => void;
};

const OPTIONS = [
    {
        label: <FormattedMessage id="search_alerts.sort.addedNewest" />,
        value: ManageSavedSearchSortOrders.DATE_ADDED_NEWEST,
    },
    {
        label: <FormattedMessage id="search_alerts.sort.resultCount" />,
        value: ManageSavedSearchSortOrders.RESULT_COUNT,
    },
    {
        label: <FormattedMessage id="search_alerts.sort.addedOldest" />,
        value: ManageSavedSearchSortOrders.DATE_ADDED_OLDEST,
    },
    {
        label: <FormattedMessage id="search_alerts.sort.searchTerm" />,
        value: ManageSavedSearchSortOrders.SEARCH_TERM,
    },
];

const ManageSavedSearchFilter = ({ filters, onFilterChange }: Props) => {
    const manageUrl = getNotificationsUrl('searches');
    const { formatMessage } = useIntl();
    const itemTab = filters.tab === 'items';
    const totalSavedSearchItems = useAppSelector(getTotalSavedSearchItems);
    const totalSavedSearchesCount = useAppSelector(getTotalSavedSearches);

    return (
        <Bar $itemTab={itemTab}>
            <FilterWrapper>
                <StyledSearchFilter
                    onChange={onFilterChange}
                    paginationFilter={filters}
                    searchPlaceholder={formatMessage(
                        {
                            id: itemTab ? 'search_alerts.resultsPlaceholder' : 'search_alerts.filterBySearchTerms',
                        },
                        {
                            count: itemTab ? totalSavedSearchItems : totalSavedSearchesCount,
                        }
                    )}
                    shouldModifyQueryParams
                />
                <SortPageSizePair
                    className="w-full"
                    hideCount
                    isInsideLabel
                    onChange={onFilterChange}
                    paginationFilter={filters}
                    shouldModifyQueryParams
                    sortLabel={<FormattedMessage id="search_alerts.sortLabel" />}
                    sortOptions={OPTIONS}
                />
            </FilterWrapper>
            <SubtleMonochromeLink
                className="xsMax:hidden"
                data-testid="manageNotifications"
                href={manageUrl}
                id="manageNotifications"
            >
                <InlineButton icon={Bell}>
                    <FormattedMessage id="manageNotifications" />
                </InlineButton>
            </SubtleMonochromeLink>
        </Bar>
    );
};

export default ManageSavedSearchFilter;

const Bar = styled.div<{ $itemTab: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 32px 0 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const FilterWrapper = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.sm};
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column-reverse;
        width: 100%;
    }
`;

const StyledSearchFilter = styled(SearchFilter)`
    width: 240px;
    label {
        display: none;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
`;
